import { Transceiver, Sensor, EntityLocation, Gateway } from '@/api';
import { Constants } from '@/common/Constants';
import { Entity } from '@/models';
import L, { Icon, LatLng, marker, Marker } from 'leaflet';
import { upperFirst, lowerCase, camelCase } from 'lodash';
import Vue from 'vue';

const siteIconUrl = require('@/assets/mapIcons/siteIcon.png');
const sensorIconUrl = require('@/assets/mapIcons/sensorIcon.png');
const nodeIconUrl = require('@/assets/mapIcons/nodeIcon.png');
const gatewayIconUrl = require('@/assets/mapIcons/gatewayIcon.png');
const transceiverIconUrl = require('@/assets/mapIcons/transceiverIcon.png');

const milkVatIconUrl = require('@/assets/mapIcons/milkvat-map-icon.png');
const fuelTankIconUrl = require('@/assets/mapIcons/fueltank-map-icon.png');
const feedTankIconUrl = require('@/assets/mapIcons/feedtank-map-icon.png');
const waterMeterIconUrl = require('@/assets/mapIcons/watermeter-map-icon.png');
const waterTankIconUrl = require('@/assets/mapIcons/watertank-map-icon.png');
const waterMonitorIconUrl = require('@/assets/mapIcons/watermonitor-map-icon.png');

export type TransceiverWithLocationData = Transceiver & {
	sensors: Sensor[];
	nodes?: Node & { sensors: Sensor[]; entitylocation?: EntityLocation }[];
	gateway?: Gateway & { entitylocation?: EntityLocation };
	entitylocation?: EntityLocation;
};

export type MarkerData = {
	marker: Marker;
	extraData?: Record<string, any>;
	popup: MarkerPopup | null;
};

export type MarkerPopup = {
	title: string;
	route: string;
	routeParams: Record<string, string>;
	linkText: string;
};

export class EntityMapService<T extends Entity> {
	public get markers(): MarkerData[] {
		return this.entityMarkers;
	}

	private entityMarkers: MarkerData[] = [];
	private makeHardwareIcon = (params: { iconUrl: any }): Icon =>
		new L.Icon({
			iconUrl: params.iconUrl,
			iconSize: [31, 46], // size of the icon
			shadowSize: [31, 46], // size of the shadow
			iconAnchor: [15, 23],
			popupAnchor: [0, -23],
		});

	private sensorIcon = this.makeHardwareIcon({ iconUrl: sensorIconUrl });
	private nodeIcon = this.makeHardwareIcon({ iconUrl: nodeIconUrl });
	private gatewayIcon = this.makeHardwareIcon({ iconUrl: gatewayIconUrl });
	private transceiverIcon = this.makeHardwareIcon({ iconUrl: transceiverIconUrl });
	private siteIcon = this.makeHardwareIcon({ iconUrl: siteIconUrl });

	constructor(
		private transceivers: TransceiverWithLocationData[],
		private entityLocations: { entityType: string; location: EntityLocation; entity: T }[],
	) {}

	public makeEntityMarkers() {
		this.makeEntityLocationMarkers();
		this.transceivers.forEach(transceiver => {
			const {
				['sensors']: transceiverSensors,
				['nodes']: nodes,
				['gateway']: gateway,
			} = transceiver;
			if (transceiver.entitylocation) {
				this.makeTransceiverMarker(transceiver);
				this.makeTransceiverSensorsMarkers(transceiverSensors, transceiver.entitylocation);
			}
			if (gateway?.entitylocation) {
				this.makeGatewayMarker(gateway, gateway.entitylocation);
			}
			if (nodes) {
				this.makeNodeMarkers(nodes);
			}
		});
	}

	public makeSiteMarker(center: { lat: number; lng: number }, siteId: string) {
		return {
			icon: this.siteIcon,
			location: center,
			entityType: 'site',
			entityId: siteId,
		};
	}

	private makeNodeMarkers(nodes: typeof this.transceivers[number]['nodes']) {
		nodes!.forEach(n => {
			let { ['sensors']: nodeSensors, ...node } = n;
			if (n.entitylocation) {
				const markerData = this.createMarker(
					n.entitylocation,
					this.nodeIcon,
					{ ...node, ...node.entitylocation },
					{
						title: 'Node',
						linkText: n.serialNumber,
						route: 'nodes.view',
						routeParams: { nodeId: n.id!.toString() },
					},
				);
				this.entityMarkers.push(markerData);
				nodeSensors.forEach(s => {
					const markerData = this.createMarker(
						n.entitylocation!,
						this.sensorIcon,
						{ entityType: 'sensors', entityId: s.id, ...s },
						{
							title: 'Node Sensor',
							linkText: Vue.filter('titleCase')(s.type ?? 'No type'),
							route: 'sensors.view',
							routeParams: { sensorId: s.id!.toString() },
						},
					);
					this.entityMarkers.push(markerData);
				});
			}
		});
	}

	private makeGatewayMarker(gateway: Gateway, gatewayLocation: EntityLocation): void {
		const markerData = this.createMarker(
			gatewayLocation,
			this.gatewayIcon,
			{ ...gateway, ...gatewayLocation },
			{
				title: 'Gateway',
				linkText: gateway.serialNumber ?? '',
				route: 'gateways.view',
				routeParams: { gatewayId: gateway.id!.toString() },
			},
		);
		this.entityMarkers.push(markerData);
	}

	private makeTransceiverSensorsMarkers(
		transceiverSensors: Sensor[],
		transceiverLocation: EntityLocation,
	): void {
		transceiverSensors
			.filter(s => s.nodeId == null)
			.forEach(s => {
				const markerData = this.createMarker(
					transceiverLocation,
					this.sensorIcon,
					{
						entityType: 'sensors',
						entityId: s.id,
						...s,
					},
					{
						title: 'Sensor',
						linkText: s.type ?? 'No type',
						route: 'sensors.view',
						routeParams: { sensorId: s.id!.toString() },
					},
				);
				this.entityMarkers.push(markerData);
			});
	}

	private makeTransceiverMarker(transceiver: typeof this.transceivers[number]): void {
		const markerData = this.createMarker(
			transceiver.entitylocation!,
			this.transceiverIcon,
			{ locationId: transceiver.entitylocation!.id },
			{
				title: 'Transceiver',
				linkText: transceiver.serialNumber ?? 'No serial number',
				route: 'transceivers.view',
				routeParams: { transceiverId: transceiver.id!.toString() },
			},
		);
		this.entityMarkers.push(markerData);
	}

	private makeEntityLocationMarkers(): void {
		if (this.entityLocations) {
			this.entityLocations.forEach(entityLocation => {
				const markerData = this.createMarker(
					entityLocation.location,
					this.makeHardwareIcon({ iconUrl: this.getEntityTypeIcon(entityLocation.entityType) }),
					{ locationId: entityLocation.location.id, entityType: entityLocation.entityType },
					{
						title: upperFirst(lowerCase(entityLocation.entityType)).slice(0, -1),
						linkText: entityLocation.entity.name ?? entityLocation.entity.label!,
						route: this.getRouteFromEntityType(entityLocation.entityType),
						routeParams: this.getRouteParamsFromEntity(
							entityLocation.entity,
							entityLocation.entityType,
						),
					},
				);
				this.entityMarkers.push(markerData);
			});
		}
	}

	private getRouteFromEntityType(entityType: string): string {
		switch (entityType) {
			case Constants.ENTITY_TYPE_VAT:
				return 'products.milk.vat';
			case Constants.ENTITY_TYPE_FUEL_TANK:
				return 'products.fuel.tank.view';
			case Constants.ENTITY_TYPE_FEED_TANK:
				return 'products.feed.feedtanks.tankdetail';
			case Constants.ENTITY_TYPE_WATER_METER:
			case Constants.ENTITY_TYPE_WATER_TANK:
			case Constants.ENTITY_TYPE_WATER_MONITOR:
				return 'products.water.entity.view';
			default:
				return '';
		}
	}

	private getRouteParamsFromEntity(entity: T, entityType: string): Record<string, string> {
		if (!entity.productId || !entity.id) {
			return {};
		}
		const entityId = entity.id!.toString();
		const productId = entity.productId!.toString();
		switch (entityType) {
			case Constants.ENTITY_TYPE_VAT:
				return { productId, vatId: entityId };
			case Constants.ENTITY_TYPE_FUEL_TANK:
				return { productId, tankId: entityId };
			case Constants.ENTITY_TYPE_FEED_TANK:
				return { productId, feedTankId: entityId };
			case Constants.ENTITY_TYPE_WATER_METER:
			case Constants.ENTITY_TYPE_WATER_TANK:
			case Constants.ENTITY_TYPE_WATER_MONITOR:
				return { productId, entityType: camelCase(entityType), entityId };
			default:
				return {};
		}
	}

	private getEntityTypeIcon(entityType: string): any {
		switch (entityType) {
			case Constants.ENTITY_TYPE_VAT:
				return milkVatIconUrl;
			case Constants.ENTITY_TYPE_FUEL_TANK:
				return fuelTankIconUrl;
			case Constants.ENTITY_TYPE_FEED_TANK:
				return feedTankIconUrl;
			case Constants.ENTITY_TYPE_WATER_METER:
				return waterMeterIconUrl;
			case Constants.ENTITY_TYPE_WATER_TANK:
				return waterTankIconUrl;
			case Constants.ENTITY_TYPE_WATER_MONITOR:
				return waterMonitorIconUrl;
			default:
				return null;
		}
	}

	private createMarker(
		location: EntityLocation,
		icon: Icon,
		extraData: Record<string, any> = {},
		popup: MarkerPopup | null = null,
	): {
		marker: Marker;
		extraData?: Record<string, any>;
		popup: MarkerPopup | null;
	} {
		const latLng: LatLng = new LatLng(location.latitude!, location.longitude!);
		return {
			marker: marker(latLng, { icon }),
			extraData,
			popup,
		};
	}
}
